'use client';

import { useEffect, useState } from 'react';

/**
 * The single maintenance page. While maintenance mode is on, the middleware
 * rewrites every page navigation here (except for admins), so no matter where
 * a user goes they see this and nothing else. It polls the status and reloads
 * automatically the moment maintenance ends — returning the user to the URL
 * they originally asked for (the rewrite preserved it).
 */
export default function MaintenancePage() {
  const [message, setMessage] = useState(
    'PACE is temporarily unavailable while we perform scheduled maintenance. All saved work and data are safe. The application will resume automatically once maintenance is complete — no action is needed. Thank you for your patience.'
  );

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch('/api/maintenance-status');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (!data.maintenance) {
          window.location.reload(); // maintenance over — the original page loads again
          return;
        }
        if (data.message) setMessage(data.message);
      } catch {
        /* keep showing the page */
      }
    };
    check();
    const id = setInterval(check, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div className='min-h-screen w-full flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 px-6'>
      <div className='max-w-lg w-full text-center rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm p-10'>
        <svg className='mx-auto mb-6 h-14 w-14 text-amber-500' viewBox='0 0 24 24' fill='none'
             stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.6z' />
        </svg>
        <h1 className='text-2xl font-semibold mb-3 text-neutral-900 dark:text-neutral-100'>
          Application under maintenance
        </h1>
        <p className='text-neutral-600 dark:text-neutral-300 mb-6'>{message}</p>
        <p className='text-sm text-neutral-400 dark:text-neutral-500'>
          This page checks automatically and will return you to the application as soon as
          maintenance completes. No action is needed.
        </p>
      </div>
    </div>
  );
}
